import { useMutation, useQuery } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export interface HeadcountByDepartment {
  department: string;
  count: number;
}

export interface MonthlyExpenses {
  month: string;
  total: number;
}

export interface TimeByProject {
  project: string;
  hours: number;
}

export interface LeaveUtilization {
  leave_type: string;
  used: number;
  total: number;
}

export interface TasksCompletionRate {
  month: string;
  rate: number;
}

async function getAuthToken(supabase: SupabaseClientLike): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const token = await getAuthToken(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...((init?.headers as Record<string, string>) ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  return res.json() as Promise<T>;
}

export const reportsKeys = {
  all: ['reports'] as const,
  headcount: (tenantId: string) => [...reportsKeys.all, 'headcount', tenantId] as const,
  expenses: (tenantId: string, months?: number) =>
    [...reportsKeys.all, 'expenses', tenantId, months ?? 6] as const,
  timeByProject: (tenantId: string, months?: number) =>
    [...reportsKeys.all, 'timeByProject', tenantId, months ?? 3] as const,
  leaveUtilization: (tenantId: string) =>
    [...reportsKeys.all, 'leaveUtilization', tenantId] as const,
  tasksCompletion: (tenantId: string, months?: number) =>
    [...reportsKeys.all, 'tasksCompletion', tenantId, months ?? 3] as const,
} as const;

export function useHeadcountByDepartment(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: reportsKeys.headcount(tenantId),
    queryFn: () =>
      apiFetch<HeadcountByDepartment[]>(supabase, 'addons/reports/headcount-by-department'),
    enabled: Boolean(tenantId),
  });
}

export function useMonthlyExpenses(supabase: SupabaseClientLike, tenantId: string, months = 6) {
  return useQuery({
    queryKey: reportsKeys.expenses(tenantId, months),
    queryFn: () =>
      apiFetch<MonthlyExpenses[]>(supabase, `addons/reports/monthly-expenses?months=${months}`),
    enabled: Boolean(tenantId),
  });
}

export function useTimeByProject(supabase: SupabaseClientLike, tenantId: string, months = 3) {
  return useQuery({
    queryKey: reportsKeys.timeByProject(tenantId, months),
    queryFn: () =>
      apiFetch<TimeByProject[]>(supabase, `addons/reports/time-by-project?months=${months}`),
    enabled: Boolean(tenantId),
  });
}

export function useTasksCompletionRate(supabase: SupabaseClientLike, tenantId: string, months = 3) {
  return useQuery({
    queryKey: reportsKeys.tasksCompletion(tenantId, months),
    queryFn: () =>
      apiFetch<TasksCompletionRate[]>(supabase, `addons/reports/tasks-completion?months=${months}`),
    enabled: Boolean(tenantId),
  });
}

export function useLeaveUtilization(supabase: SupabaseClientLike, tenantId: string) {
  return useQuery({
    queryKey: reportsKeys.leaveUtilization(tenantId),
    queryFn: () => apiFetch<LeaveUtilization[]>(supabase, 'addons/reports/leave-utilization'),
    enabled: Boolean(tenantId),
  });
}

// ---------------------------------------------------------------------------
// L84 analytics report types
// ---------------------------------------------------------------------------

export interface HeadcountReport {
  total_employees: number;
  active_employees: number;
  on_leave: number;
  new_hires_this_period: number;
  departures_this_period: number;
  by_department: Array<{ dept: string; count: number }>;
  by_status: Array<{ status: string; count: number }>;
  trend_data: Array<{ date: string; count: number }>;
}

export interface PayrollReport {
  total_spent: number;
  average_salary: number;
  salary_by_department: Array<{ dept: string; total: number; avg: number }>;
  bonus_pool: number;
  tax_withheld: number;
  trend_data: Array<{ date: string; spent: number }>;
}

export interface AttendanceReport {
  total_hours_worked: number;
  average_daily_hours: number;
  attendance_rate: number;
  on_time_rate: number;
  late_arrivals: number;
  absences: number;
  by_employee: Array<{ employee_id: string; hours: number; rate: number }>;
}

export interface TurnoverReport {
  departures_this_period: number;
  departure_rate: number;
  by_department: Array<{ dept: string; count: number }>;
  reasons: Array<{ reason: string; count: number }>;
  average_tenure_months: number;
}

export interface ExportJobResult {
  job_id: string;
  status: string;
  report_type: string;
  format: string;
  message: string;
}

// ---------------------------------------------------------------------------
// L84 hooks — call /functions/v1/reports/* via apiFetch
// ---------------------------------------------------------------------------

const analyticsReportKeys = {
  headcount: (tenantId: string, start: string, end: string) =>
    ['analytics-reports', 'headcount', tenantId, start, end] as const,
  payroll: (tenantId: string, start: string, end: string) =>
    ['analytics-reports', 'payroll', tenantId, start, end] as const,
  attendance: (tenantId: string, start: string, end: string) =>
    ['analytics-reports', 'attendance', tenantId, start, end] as const,
  turnover: (tenantId: string, start: string, end: string) =>
    ['analytics-reports', 'turnover', tenantId, start, end] as const,
};

export function useAnalyticsHeadcountReport(
  supabase: SupabaseClientLike,
  tenantId: string,
  startDate: string,
  endDate: string,
) {
  return useQuery({
    queryKey: analyticsReportKeys.headcount(tenantId, startDate, endDate),
    queryFn: () =>
      apiFetch<HeadcountReport>(
        supabase,
        `reports/headcount?tenant_id=${tenantId}&start=${startDate}&end=${endDate}`,
      ),
    enabled: Boolean(tenantId),
  });
}

export function useAnalyticsPayrollReport(
  supabase: SupabaseClientLike,
  tenantId: string,
  startDate: string,
  endDate: string,
) {
  return useQuery({
    queryKey: analyticsReportKeys.payroll(tenantId, startDate, endDate),
    queryFn: () =>
      apiFetch<PayrollReport>(
        supabase,
        `reports/payroll?tenant_id=${tenantId}&start=${startDate}&end=${endDate}`,
      ),
    enabled: Boolean(tenantId),
  });
}

export function useAnalyticsAttendanceReport(
  supabase: SupabaseClientLike,
  tenantId: string,
  startDate: string,
  endDate: string,
) {
  return useQuery({
    queryKey: analyticsReportKeys.attendance(tenantId, startDate, endDate),
    queryFn: () =>
      apiFetch<AttendanceReport>(
        supabase,
        `reports/attendance?tenant_id=${tenantId}&start=${startDate}&end=${endDate}`,
      ),
    enabled: Boolean(tenantId),
  });
}

export function useAnalyticsTurnoverReport(
  supabase: SupabaseClientLike,
  tenantId: string,
  startDate: string,
  endDate: string,
) {
  return useQuery({
    queryKey: analyticsReportKeys.turnover(tenantId, startDate, endDate),
    queryFn: () =>
      apiFetch<TurnoverReport>(
        supabase,
        `reports/turnover?tenant_id=${tenantId}&start=${startDate}&end=${endDate}`,
      ),
    enabled: Boolean(tenantId),
  });
}

export function useAnalyticsExportReport(supabase: SupabaseClientLike, tenantId: string) {
  return useMutation({
    mutationFn: ({
      reportType,
      format,
      startDate,
      endDate,
    }: {
      reportType: string;
      format: 'pdf' | 'csv' | 'xlsx';
      startDate: string;
      endDate: string;
    }) =>
      apiFetch<ExportJobResult>(supabase, 'reports/export', {
        method: 'POST',
        body: JSON.stringify({
          report_type: reportType,
          format,
          tenant_id: tenantId,
          start: startDate,
          end: endDate,
        }),
      }),
  });
}
